import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { ROUTES } from "@/app/routes";
import { resolveCrumbs } from "@/shared/components/layout/topbar-breadcrumb.titles";

export type { Crumb } from "@/shared/components/layout/topbar-breadcrumb.titles";
/// Re-exported so `topbar.tsx` and anything else keeps one import path for the
/// breadcrumb, whichever half it wants.
export { resolveCrumbs } from "@/shared/components/layout/topbar-breadcrumb.titles";

interface Props {
  pathname: string;
  workspaceName: string;
  workspaceSlug: string;
  isResolved: boolean;
  trailing: string | null;
}

export function TopbarBreadcrumb({
  pathname,
  workspaceName,
  workspaceSlug,
  isResolved,
  trailing,
}: Props): ReactNode {
  const crumbs = resolveCrumbs(pathname, workspaceSlug, Boolean(trailing) && isResolved);
  return (
    <nav
      aria-label="Breadcrumb"
      className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden text-sm text-text-tertiary"
    >
      <Link
        to={ROUTES.workspace.home.path}
        params={{ workspaceSlug }}
        className="min-w-0 truncate transition-colors hover:text-foreground"
      >
        {workspaceName}
      </Link>
      {!isResolved ? (
        <>
          <Separator />
          <span className="shrink-0 font-semibold text-foreground">Workspace unavailable</span>
        </>
      ) : null}
      {crumbs.map((crumb, i) => {
        const isLast = i === crumbs.length - 1 && !trailing;
        return (
          <span key={crumb.label} className="flex min-w-0 shrink-0 items-center gap-2">
            <Separator />
            {crumb.href && !isLast ? (
              <a
                href={crumb.href}
                className="shrink-0 text-text-tertiary transition-colors hover:text-foreground"
              >
                {crumb.label}
              </a>
            ) : (
              <span
                className={
                  isLast ? "shrink-0 font-semibold text-foreground" : "shrink-0 text-text-tertiary"
                }
              >
                {crumb.label}
              </span>
            )}
          </span>
        );
      })}
      {trailing ? (
        <>
          <Separator />
          <span title={trailing} className="min-w-0 truncate font-semibold text-foreground">
            {trailing}
          </span>
        </>
      ) : null}
    </nav>
  );
}

function Separator(): ReactNode {
  return (
    <span className="text-text-quaternary" aria-hidden="true">
      /
    </span>
  );
}
